
import { Navigation } from "@/components/layout/Navigation";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { BadgesSection } from "@/components/dashboard/badges/BadgesSection";
import { LevelProgress } from "@/components/dashboard/rewards/LevelProgress";
import { LevelsList } from "@/components/dashboard/rewards/LevelsList";

const Recompensas = () => {
  const [openLevels, setOpenLevels] = useState<number[]>([]);

  const toggleLevel = (levelId: number) => {
    setOpenLevels(prev => 
      prev.includes(levelId) 
        ? prev.filter(id => id !== levelId)
        : [...prev, levelId]
    );
  };

  const { data: userSettings } = useQuery({
    queryKey: ["user-settings"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data } = await supabase
        .from('user_settings')
        .select('level')
        .eq('user_id', user.id)
        .single();

      return data;
    }
  });

  const { data: badgeCount } = useQuery({
    queryKey: ["badge-count"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const { count } = await supabase
        .from('user_badges')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id);

      return count || 0;
    }
  });

  const getLevelProgress = () => {
    if (!badgeCount) return 0;
    
    if (badgeCount >= 50) return 100;
    if (badgeCount >= 20) return Math.min(((badgeCount - 20) / 30) * 100, 100);
    if (badgeCount >= 10) return Math.min(((badgeCount - 10) / 10) * 100, 100);
    return Math.min((badgeCount / 10) * 100, 100);
  };

  const getNextLevelRequirement = () => {
    if (!badgeCount) return 10;
    if (badgeCount < 10) return 10;
    if (badgeCount < 20) return 20;
    if (badgeCount < 50) return 50;
    return 50;
  };

  return (
    <div className="min-h-screen bg-background text-primary">
      <div className="container pb-24">
        <header className="sticky top-0 z-40 bg-background border-b border-border">
          <div className="container flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold text-primary">Recompensas</h1>
          </div>
        </header>

        <LevelProgress
          badgeCount={badgeCount}
          getNextLevelRequirement={getNextLevelRequirement}
          getLevelProgress={getLevelProgress}
          userLevel={userSettings?.level}
        />

        <Tabs defaultValue="badges" className="mt-6">
          <TabsList className="w-full">
            <TabsTrigger value="badges" className="w-1/2 text-primary">Logros</TabsTrigger>
            <TabsTrigger value="levels" className="w-1/2 text-primary">Niveles</TabsTrigger>
          </TabsList>

          <TabsContent value="badges" className="mt-6">
            <BadgesSection />
          </TabsContent>

          <TabsContent value="levels" className="mt-6">
            <LevelsList
              badgeCount={badgeCount}
              userLevel={userSettings?.level}
              openLevels={openLevels}
              toggleLevel={toggleLevel}
            />
          </TabsContent>
        </Tabs>
      </div>
      <Navigation />
    </div>
  );
};

export default Recompensas;
